import React, { useCallback, useEffect, useState } from 'react'
import debounce from 'lodash/debounce'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import {
  AiAutomationLayoutActions,
  AiAutomationLayoutSelectors,
} from 'apps/aiAutomation/store/layout'

export const useLayoutShift = ({
  sidebarRef,
}: {
  sidebarRef: React.RefObject<HTMLDivElement | null>
}) => {
  const dispatch = useAppDispatch()

  const isSidebarOpen = useAppSelector(AiAutomationLayoutSelectors.getIsSidebarOpen)

  const [defaultValuesSet, setDefaultValuesSet] = useState(false)

  const updateLayoutShift = useCallback(
    (isSidebarOpen: boolean) => {
      if (!sidebarRef.current) {
        return
      }

      const sidebarTranslateX = isSidebarOpen ? 0 : sidebarRef.current.clientWidth

      sidebarRef.current.style.transform = `translateX(${sidebarTranslateX}px)`
    },
    [sidebarRef],
  )

  useEffect(() => {
    updateLayoutShift(isSidebarOpen)
  }, [updateLayoutShift, isSidebarOpen])

  useEffect(() => {
    setDefaultValuesSet(true)
  }, [])

  useEffect(() => {
    if (!isSidebarOpen) {
      return
    }

    const debouncedUpdateLayoutShift = debounce(() => updateLayoutShift(isSidebarOpen), 200)

    window.addEventListener('resize', debouncedUpdateLayoutShift)

    return () => {
      window.removeEventListener('resize', debouncedUpdateLayoutShift)
    }
  }, [isSidebarOpen, updateLayoutShift])

  useEffect(() => {
    if (isSidebarOpen || !sidebarRef.current) {
      return
    }

    const sidebarElement = sidebarRef.current

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.currentTarget === sidebarElement && event.propertyName === 'transform') {
        dispatch(AiAutomationLayoutActions.viewStateReset())

        sidebarElement.removeEventListener('transitionend', onTransitionEnd)
      }
    }

    sidebarElement.addEventListener('transitionend', onTransitionEnd)

    return () => {
      sidebarElement.removeEventListener('transitionend', onTransitionEnd)
    }
  }, [dispatch, isSidebarOpen, sidebarRef])

  return {
    defaultValuesSet,
  }
}
